/**
 * Il configuratore di un campo (CF05) — CL02.
 *
 * Rende i parametri che `FIELD_CONTROLS` dichiara per il tipo, con la specie
 * che `CONTROL_SPECS` gli assegna. Non sa niente di tipi: aggiungere un tipo
 * significa aggiungere una riga alle due tabelle, non toccare questo file.
 *
 * Tre scelte che si vedono nel codice:
 *
 * - **I decimali restano stringhe.** `min`, `max` e `displayFactor` sono
 *   decimali canonici nel contratto (`"1000.5"`). Portarli per `Number` qui
 *   reintrodurrebbe il float che il trasporto evita, e un importo diventerebbe
 *   `1000.4999999999999` al primo giro.
 * - Mostra gli errori dopo Salva e blocca i problemi gia' noti. Il verdetto
 *   definitivo resta del server quando si salva il modello.
 * - **Non finge.** Un parametro che non si sa rendere viene **detto**, non reso
 *   con un campo di testo: un testo accetterebbe un valore che il server
 *   rifiuta, e il rifiuto arriverebbe lontano dalla sua causa. La formula ha il
 *   suo editor (`FormulaConfiguration`), che della validazione non promette
 *   niente in locale.
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useMemo, useState } from "react";
import DialogHeader from "components/dialogs/DialogHeader";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import { getTranslation } from "common";
import { useSelectedOrganization, useTranslations } from "hooks";
import type { FieldDefinition, OptionDefinition } from "model/CustomFields";
import { validateFieldConfig, configProblemsByParam } from "./fieldConfig";
import { translateMessage } from "./messages";
import { choiceConfigIssues } from "./fieldTypes";
import { optionIssues } from "./choiceOptions";
import { choiceLabelKey, controlsFor, type ResolvedControl } from "./controlKinds";
import { currencyByCode, symbolFor } from "./currencies";
import ChoiceConfiguration from "./ChoiceConfiguration";
import FormulaConfiguration from "./FormulaConfiguration";
import type { FormulaPreviewOutcome } from "./FormulaConfiguration";
import SequenceConfiguration from "./SequenceConfiguration";

/**
 * Il testo di una voce di dizionario, o il ripiego scritto nel codice.
 *
 * `getTranslation` fa `translations[key] || key`, quindi una voce che il
 * backend non ha ancora restituito **non** torna vuota: torna la chiave. Senza
 * riconoscerlo, il ripiego non scatterebbe mai e a schermo si leggerebbe
 * `custom_fields.config.expression.label` — un identificatore al posto di una
 * frase. Il valore coincide con il default solo quando e' la chiave ancora non
 * tradotta.
 */
const text = (
  translations: Record<string, string>,
  key: string,
  fallback: string
): string => {
  const resolved = getTranslation(translations, key);
  return !resolved || resolved === key ? fallback : resolved;
};

/**
 * I parametri della formula, gli unici che portano una (i) accanto alla label.
 *
 * `outputType` non c'e': non e' piu' un parametro che si compila. Il tipo lo
 * legge il server dalla formula, e l'editor lo mostra in sola lettura.
 */
const FORMULA_CONTROLS = ["expression", "precision"] as const;

/**
 * Le informative dei parametri della formula.
 *
 * Il testo sta nel codice **con la stessa chiave** che userà il dizionario: così
 * la voce che il backend pubblicherà la sostituisce senza toccare altro, e nel
 * frattempo il progettista legge una frase invece di un identificatore.
 *
 * La domanda che ognuna risponde è quella che un progettista si fa davvero —
 * «cosa scrivo qui», «perché il server ha bisogno di saperlo» — non una
 * descrizione del parametro.
 */
const INFORMATIVE: Record<string, { labelKey: string; bodyKey: string; label: string; body: string }> = {
  expression: {
    labelKey: "custom_fields.config.expression.label",
    bodyKey: "custom_fields.config.expression.help",
    label: "Espressione",
    body:
      "È il calcolo che il campo eseguirà. Scrivi i nomi degli altri campi fra parentesi graffe, " +
      "per esempio {Importo} * {Quantità}, e usa gli operatori normali (+, -, *, /). " +
      "Funzioni come SUM o ROUND si scrivono con le parentesi tonde: SUM({Importo}; {Tassa}). " +
      "Il risultato non viene calcolato qui: lo calcola il server, che è l'unico a sapere i valori veri.",
  },
  precision: {
    labelKey: "custom_fields.config.precision.label",
    bodyKey: "custom_fields.config.precision.help",
    label: "Decimali",
    body:
      "Quante cifre dopo la virgola si vedono e si confrontano. Con 2, un totale di 12,5 si legge 12,50. " +
      "Non cambia il calcolo: cambia solo come il risultato viene presentato e confrontato con gli altri.",
  },
};

/**
 * Che cosa è un campo calcolato, in una frase che non presuppone niente.
 *
 * Sta in testa al configuratore perché è la prima cosa che manca: senza, si
 * scrive una formula senza sapere chi la esegue, quando, e perché serve anche
 * dire il tipo del risultato.
 */
const INTRO_CALCOLATO = {
  key: "calculated_field_helper",
  fallback:
    "Questo campo non si compila a mano, ma viene calcolato usando gli altri campi " +
    "Tu scrivi solo la formula, ad esempio {Campo1} * {Campo2}. "
};

interface FieldConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  field: FieldDefinition | null;
  onSave: (field: FieldDefinition) => void;
  /**
   * Il modello su cui il server valida le formule. Assente finche' non e'
   * salvato: la validazione non si puo' inventare in locale.
   */
  modelId?: string;
  /** Gli altri campi del modello: una formula puo' riferirli. */
  fields?: readonly FieldDefinition[];
  additionalFields?: readonly FieldDefinition[];
}

const FieldConfigurationDialog: React.FC<FieldConfigurationDialogProps> = ({
  open,
  onClose,
  field,
  onSave,
  modelId,
  fields = [],
  additionalFields,
}) => {
  const translations = useTranslations();
  // Il registro si sceglie fra quelli dell'organizzazione selezionata: un id
  // scritto a mano sarebbe un registro che il server non riconosce.
  const organizationId = String(useSelectedOrganization()?.id ?? "");
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [formulaOutcome, setFormulaOutcome] = useState<FormulaPreviewOutcome>("empty");

  // La bozza riparte dal campo ogni volta che il dialog si apre su un altro
  // campo, o sullo stesso dopo un salvataggio. L'esito della formula non si
  // azzera qui: l'editor lo riferisce da se' al mount, e un reset da questo
  // effetto (che gira dopo quelli dei figli) cancellerebbe quel verdetto.
  useEffect(() => {
    setConfig(field ? { ...field.config } : {});
    setSaveAttempted(false);
  }, [field, open]);

  const { resolved, unknown } = useMemo(
    () => controlsFor(field?.type ?? "text"),
    [field?.type]
  );

  const problems = useMemo(
    () => (field ? validateFieldConfig(field.type, config) : []),
    [field, config]
  );
  const choiceProblems = useMemo(
    () => (field && ["select", "multiselect", "radio"].includes(field.type)
      ? [...choiceConfigIssues(config), ...optionIssues(
        Array.isArray(config.options) ? config.options as OptionDefinition[] : []
      ).map(({ issue }) => issue)]
      : []),
    [field, config]
  );
  const perParametro = useMemo(
    () => (field ? configProblemsByParam(field.type, config) : {}),
    [field, config]
  );

  /**
   * I problemi dell'anteprima che il riepilogo nomina.
   *
   * Le diagnostiche restano nell'editor — tradotte, con la posizione — perche'
   * li' c'e' lo spazio per leggerle. Qui basta la ragione per cui Salva non
   * chiude: espressione invalida, anteprima in corso, anteprima non riuscita.
   * `empty` lo dice gia' `expression_required`; `unavailable` (modello non
   * ancora salvato) e `valid` non sono problemi.
   */
  const formulaProblems = useMemo(() => {
    if (field?.type !== "calculated") return [];
    if (formulaOutcome === "invalid") return ["custom_fields.formula.invalid"];
    if (formulaOutcome === "pending") return ["custom_fields.formula.preview_pending"];
    if (formulaOutcome === "failed") return ["custom_fields.formula.preview_failed"];
    return [];
  }, [field?.type, formulaOutcome]);

  /**
   * Il testo di un problema, coi suoi parametri.
   *
   * `custom_fields.error.invalid_range` porta `{min}` e `{max}` nel catalogo —
   * la diagnosi "minimo oltre massimo" li conosce e li porta con sé
   * (`configProblemsByParam.params`) — e senza di loro il riepilogo mostrerebbe
   * il template alla lettera. Un problema senza attribuzione, o senza parametri,
   * si rende com'è.
   */
  const problemText = (key: string): string =>
    translateMessage(
      getTranslation(translations, key),
      Object.values(perParametro).find((problem) => problem.key === key)?.params
    );

  // I registri gia' presi dagli **altri** campi del modello: la tendina non li
  // offre, cosi' due `autoincrement` non finiscono sullo stesso contatore
  // rubandosi i numeri. Il campo corrente e' escluso: sceglie per se'.
  const usedRegistryIds = useMemo(() => {
    if (!field || field.type !== "autoincrement") return [];
    return fields
      .filter((other) => other.fieldId !== field.fieldId && other.type === "autoincrement")
      .map((other) => other.config.registryId)
      .filter((id): id is string => typeof id === "string" && id !== "");
  }, [fields, field]);

  if (!field) return null;

  /** Scrive un parametro, o lo **toglie** quando il valore e' `undefined`. */
  const set = (name: string, value: unknown) => {
    setConfig((prev) => {
      if (value === undefined || value === "") {
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: value };
    });
  };

  const render = (control: ResolvedControl) => {
    const { name, spec } = control;
    const label = text(translations, control.labelKey, control.labelKey);
    const help = text(translations, control.helpKey, "");
    const value = config[name];
    /** Il problema **di questo** parametro, se ce n'è uno. */
    const problem = saveAttempted ? perParametro[name] : undefined;
    // I parametri della formula portano la (i) accanto alla label: la spiegazione
    // lunga non sta nel campo, e un testo lungo sotto un campo stretto non lo
    // legge nessuno.
    const conInfo = FORMULA_CONTROLS.includes(name as never) ? INFORMATIVE[name] : undefined;

    const common = {
      size: "small" as const,
      fullWidth: true,
      // La label può portare la (i): diventa un nodo, non una stringa, quando la
      // spiegazione lunga non sta nel campo.
      label: conInfo ? (
        <Stack direction="row" alignItems="center" component="span" gap={0.5}>
          <span>{label}</span>
          <UserDataFieldsInfo
            labelKey={conInfo.labelKey}
            bodyKey={conInfo.bodyKey}
            triggerSx={{ ml: 0.5 }}
            stopPropagation
          />
        </Stack>
      ) : (
        label
      ),
      // Il testo dice cosa fa il parametro. Il problema **non** si scrive più
      // sotto il campo: il rosso sull'input dice che c'è, e il riepilogo in
      // fondo alla dialog lo nomina per esteso.
      helperText: help || undefined,
      error: Boolean(problem),
      onKeyUp: (e: React.KeyboardEvent) => e.stopPropagation(),
      inputProps: { "data-testid": `cf-config-${name}` },
    };

    switch (spec.kind) {
      case "text": {
        // Il simbolo non si scrive: lo detta la valuta. Su un campo
        // configurato prima della lista resta vuoto nel config, quindi qui si
        // mostra quello del codice e il salvataggio lo rende esplicito.
        const locked = name === "symbol";
        const derived = symbolFor(config.currencyCode as string);
        const shown = locked
          ? derived ?? (typeof value === "string" ? value : "")
          : typeof value === "string"
            ? value
            : "";
        return (
          <TextField
            {...common}
            key={name}
            value={shown}
            disabled={locked}
            inputProps={{
              ...common.inputProps,
              maxLength: spec.maxLength,
            }}
            onChange={(e) => set(name, e.target.value)}
          />
        );
      }

      case "integer":
        return (
          <TextField
            {...common}
            key={name}
            type="number"
            value={typeof value === "number" ? String(value) : ""}
            inputProps={{
              ...common.inputProps,
              min: spec.min,
              max: spec.max,
            }}
            // Vuoto significa "non impostato", e allora il parametro sparisce
            // dal config invece di diventare zero: e' il server ad applicare il
            // suo default, e lo fa meglio di un numero scelto qui.
            onChange={(e) =>
              set(name, e.target.value === "" ? undefined : Number(e.target.value))
            }
          />
        );

      case "decimal":
        return (
          <TextField
            {...common}
            key={name}
            // Stringa, non `type="number"`: il contratto trasporta la grafia
            // canonica del decimale, e il browser la normalizzerebbe a float.
            value={typeof value === "string" ? value : ""}
            placeholder="0.00"
            onChange={(e) => set(name, e.target.value)}
          />
        );

      case "boolean":
        return (
          <FormControlLabel
            key={name}
            label={label}
            control={
              <Checkbox
                size="small"
                inputProps={
                  { "data-testid": `cf-config-${name}` } as React.InputHTMLAttributes<HTMLInputElement>
                }
                checked={value === true}
                onChange={(e, checked) => set(name, checked)}
              />
            }
          />
        );

      case "select": {
        // Un codice valuta scritto prima della lista resta selezionabile: la
        // select non mostra un vuoto e il salvataggio non lo perde.
        const choices =
          name === "currencyCode" &&
          typeof value === "string" &&
          value !== "" &&
          !spec.choices.includes(value)
            ? [...spec.choices, value]
            : spec.choices;
        return (
          <TextField
            {...common}
            key={name}
            select
            // `inputProps` dello spread finirebbe sull'input nativo, che e'
            // `aria-hidden`: si sostituisce, non si aggiunge, altrimenti due
            // elementi porterebbero lo stesso testid.
            inputProps={undefined}
            SelectProps={
              { "data-testid": `cf-config-${name}` } as never
            }
            value={typeof value === "string" ? value : ""}
            onChange={(e) => {
              const next = e.target.value;
              set(name, next);
              // La valuta sceglie il simbolo: il campo del simbolo è in sola
              // lettura, e cambiando valuta si riallinea da sé.
              if (name === "currencyCode") {
                const symbol = symbolFor(next);
                if (symbol) set("symbol", symbol);
              }
            }}
          >
            {choices.map((choice) => {
              // Una parola si traduce, un simbolo si mostra: `choiceLabelKey`
              // decide quale delle due, invece di comporre una chiave con
              // dentro la punteggiatura (`…choice.,`) che non esiste.
              const chiave = choiceLabelKey(name, choice);
              const currency = currencyByCode(choice);
              return (
                <MenuItem key={choice} value={choice}>
                  {currency
                    ? `${currency.code} — ${currency.label}`
                    : chiave
                      ? getTranslation(translations, chiave)
                      : choice}
                </MenuItem>
              );
            })}
          </TextField>
        );
      }

      case "options":
        return (
          <Box key={name}>
            <Typography variant="subtitle2" data-testid={`cf-config-${name}`}>
              {label}
            </Typography>
            <ChoiceConfiguration
              showErrors={saveAttempted}
              // Il flag «Dinamico» è della scelta multipla: chiave e valore
              // per ogni voce.
              allowDynamic={field.type === "multiselect"}
              dynamic={config.dynamic === true}
              onDynamicChange={(dynamic) => set("dynamic", dynamic)}
              options={
                Array.isArray(config.options)
                  ? (config.options as never)
                  : []
              }
              onChange={(next) => setConfig((prev) => ({ ...prev, options: next }))}
            />
          </Box>
        );

      case "default":
        // Il default di una scelta e' l'id di un'opzione, e si sceglie da un
        // elenco: un campo di testo lascerebbe scrivere un id che non esiste.
        if (["select", "multiselect", "radio"].includes(field.type)) {
          const options = Array.isArray(config.options)
            ? (config.options as { id: string; label: string }[])
            : [];
          // `multiselect` vuole un array di id, non un id: la stessa select
          // singola che serve `select`/`radio` gli manderebbe una stringa, e il
          // server la rifiuta (`MultiselectConfig.default` e' una tupla). Con
          // `multiple` il campo tiene l'array da se', quindi le due forme non
          // possono piu' confondersi.
          const multiplo = field.type === "multiselect";
          return (
            // L'array arriva da `e.target.value`, che con `multiple` e' gia' un
            // `string[]`: `value` dev'essere un array anche vuoto, altrimenti
            // React passa da controllato a non controllato al primo tocco.
            <TextField
              {...common}
              key={name}
              select
              inputProps={undefined}
              SelectProps={
                {
                  multiple: multiplo,
                  "data-testid": `cf-config-${name}`,
                } as never
              }
              disabled={options.length === 0}
              value={multiplo ? (Array.isArray(value) ? value : []) : typeof value === "string" ? value : ""}
              onChange={(e) => set(name, e.target.value)}
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        if (field.type === "boolean") {
          return (
            <FormControlLabel
              key={name}
              label={label}
              control={
                <Checkbox
                  size="small"
                  inputProps={
                    { "data-testid": `cf-config-${name}` } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={value === true}
                  onChange={(e, checked) => set(name, checked)}
                />
              }
            />
          );
        }
        return (
          <TextField
            {...common}
            key={name}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => set(name, e.target.value)}
          />
        );

      case "registry":
        return (
          <SequenceConfiguration
            key={name}
            organizationId={organizationId}
            // Il codice di un progressivo locale nasce dal nome del campo:
            // non è un'identità da inventare, e qui è quello che si sta
            // configurando.
            fieldLabel={field.label}
            // I due autoincrementi: l'organizzazione sempre, i locali di
            // questo modello. Un modello non salvato non ne ha.
            modelId={modelId ?? null}
            value={typeof value === "string" ? value : null}
            onChange={(registryId) => set(name, registryId)}
            usedRegistryIds={usedRegistryIds}
          />
        );

      case "expression": {
        // La formula si scrive nell'editor suo (CL04): un campo di testo
        // accetterebbe qualunque cosa, e il rifiuto arriverebbe lontano dalla
        // sua causa.
        //
        // Nessuno offre se stesso come token (sarebbe un ciclo) e un campo
        // senza label non e' un campo.
        const riferibili = fields.filter(
          (candidate) =>
            candidate.fieldId !== field.fieldId && candidate.label.trim() !== ""
        );
        return (
          <Box key={name}>
            <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
              <Typography variant="body2">{label}</Typography>
              {conInfo && (
                <UserDataFieldsInfo
                  labelKey={conInfo.labelKey}
                  bodyKey={conInfo.bodyKey}
                  triggerSx={{ ml: 0.5 }}
                  stopPropagation
                />
              )}
            </Stack>
            <FormulaConfiguration
              key={`${field.fieldId}-${open}`}
              showErrors={saveAttempted}
              onPreviewStateChange={setFormulaOutcome}
              modelId={modelId}
              fields={riferibili}
              additionalFields={additionalFields}
              label={label}
              outputType={typeof config.outputType === "string" ? config.outputType : undefined}
              precision={typeof config.precision === "number" ? config.precision : undefined}
              value={typeof value === "string" ? value : ""}
              onChange={(next) => set(name, next)}
              // Il tipo lo dichiara il server, e il config lo conserva: e' la
              // dichiarazione che il server ha verificato insieme alla formula.
              onOutputTypeChange={(tipo) => set("outputType", tipo)}
            />
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      id="field-configuration-dialog"
      data-testid="cf-config-dialog"
    >
      <DialogHeader sx={{ px: 4 }} onClose={onClose}>
        {/* `{field}` è un segnaposto del catalogo, non testo da mostrare. */}
        {translateMessage(
          getTranslation(translations, "custom_fields.config.title"),
          { field: field.label }
        )}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>

          {field.type === "calculated" && (
            // La prima cosa che mancava: chi esegue il calcolo, quando, e perché
            // serve anche dire il tipo del risultato. Prima di questa riga il
            // dialog chiedeva una formula senza dire che cosa ne sarebbe stato.
            <Alert severity="info" data-testid="cf-config-calculated-intro" sx={{ alignItems: "flex-start" }}>
              {text(translations, INTRO_CALCOLATO.key, INTRO_CALCOLATO.fallback)}
            </Alert>
          )}

          {unknown.length > 0 && (
            // Se compare, l'invariante fra le due tabelle e' rotta: meglio
            // dirlo che configurare meta' campo in silenzio.
            <Alert severity="warning" data-testid="cf-config-unknown">
              {getTranslation(translations, "custom_fields.config.unknown_params")}
              {": "}
              {unknown.join(", ")}
            </Alert>
          )}

          {resolved.map(render)}

          {saveAttempted &&
            (problems.length > 0 || choiceProblems.length > 0 || formulaProblems.length > 0) && (
            <Alert severity="error" data-testid="cf-config-problems">
              {/* `Array.from` e non lo spread: il `Set` non e' iterabile con il
                  target di questo progetto. */}
              {Array.from(new Set([...problems, ...choiceProblems, ...formulaProblems]))
                .map(problemText)
                .join(" · ")}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="cf-config-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <Tooltip
          title={
            saveAttempted
              ? problems.length > 0
                ? problemText(problems[0])
                : formulaProblems.length > 0
                  ? getTranslation(translations, formulaProblems[0])
                  : ""
              : ""
          }
        >
          <span>
            <LoadingButton
              onClick={() => {
                setSaveAttempted(true);
                // Salva solo con un verdetto `valid` del server. `unavailable`
                // (modello non ancora salvato) resta salvabile; l'espressione
                // vuota la blocca gia' `expression_required`.
                if (problems.length || choiceProblems.length || Object.keys(perParametro).length || formulaProblems.length) return;
                // Il nome del campo non si tocca qui: si scrive nella riga
                // della master, per tutti i tipi allo stesso modo.
                // Il simbolo segue la valuta anche quando il campo arrivava da
                // una configurazione vecchia senza simbolo: si salva esplicito.
                onSave({
                  ...field,
                  config: (field.type === "currency"
                    ? { ...config, symbol: symbolFor(config.currencyCode as string) ?? config.symbol }
                    : config) as never,
                });
              }}
              data-testid="cf-config-save"
            >
              {getTranslation(translations, "pm.button.save")}
            </LoadingButton>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default FieldConfigurationDialog;
